"use client"

import { X, FileText, Download, Users, FolderPlus, Lock, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { DELIVERABLE_TITLES, DELIVERABLE_PERMISSIONS } from "@/lib/stores/deliverable-store"
import { useUserStore } from "@/lib/stores/user-store"
import type { SectionSlug } from "@/lib/types/sections"

interface DeliverableReadyModalProps {
  sectionSlug: SectionSlug
  onClose: () => void
  onViewReport: () => void
  onUpgrade?: () => void
}

export function DeliverableReadyModal({ sectionSlug, onClose, onViewReport, onUpgrade }: DeliverableReadyModalProps) {
  const { tier } = useUserStore()
  const permissions = DELIVERABLE_PERMISSIONS[tier]
  const title = DELIVERABLE_TITLES[sectionSlug]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[var(--surface)] border border-[var(--border-light)] rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-[var(--cta)] to-[var(--ai-violet)] p-6 text-white">
          <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/20 motion-fast">
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Sparkles className="h-6 w-6" />
            </div>
            <h2 className="text-xl font-semibold">Your Strategy Report Is Ready</h2>
          </div>
          <p className="text-white/80 text-sm">
            Based on your inputs, we created a tailored strategy report for this section.
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center gap-3 p-4 bg-[var(--background)] rounded-lg border border-[var(--border-light)] mb-6">
            <FileText className="h-8 w-8 text-[var(--cta)]" strokeWidth={1.5} />
            <div>
              <p className="text-body-medium text-[var(--text-primary)]">{title}</p>
              <p className="text-caption text-[var(--text-secondary)]">Just generated</p>
            </div>
          </div>

          <div className="space-y-3">
            {/* View Report - Available to all */}
            <Button
              onClick={onViewReport}
              className="w-full min-h-[48px] bg-[var(--cta)] hover:bg-[var(--cta-hover)] text-white rounded-lg gap-2"
            >
              <FileText className="h-5 w-5" />
              {permissions.canViewFull ? "View Report" : "View Preview"}
            </Button>

            {/* Download PDF */}
            {permissions.canDownloadPdf ? (
              <Button
                variant="outline"
                className="w-full min-h-[48px] border-[var(--border-medium)] hover:bg-[var(--surface)] rounded-lg gap-2 bg-transparent"
              >
                <Download className="h-5 w-5" />
                Download PDF
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={onUpgrade}
                className="w-full min-h-[48px] border-[var(--border-medium)] hover:bg-[var(--surface)] rounded-lg gap-2 opacity-70 bg-transparent"
              >
                <Lock className="h-4 w-4" />
                Download PDF
                <span className="ml-auto text-xs text-[var(--cta)]">Upgrade to Starter</span>
              </Button>
            )}

            {/* Share with Team */}
            {permissions.canShareWithTeam ? (
              <Button
                variant="outline"
                className="w-full min-h-[48px] border-[var(--border-medium)] hover:bg-[var(--surface)] rounded-lg gap-2 bg-transparent"
              >
                <Users className="h-5 w-5" />
                Share with Team
              </Button>
            ) : tier === "starter" ? (
              <Button
                variant="outline"
                onClick={onUpgrade}
                className="w-full min-h-[48px] border-[var(--border-medium)] hover:bg-[var(--surface)] rounded-lg gap-2 opacity-70 bg-transparent"
              >
                <Lock className="h-4 w-4" />
                Share with Team
                <span className="ml-auto text-xs text-[var(--cta)]">Upgrade to Team</span>
              </Button>
            ) : null}

            {/* Save to Folder */}
            {permissions.canSaveToFolder ? (
              <Button
                variant="outline"
                className="w-full min-h-[48px] border-[var(--border-medium)] hover:bg-[var(--surface)] rounded-lg gap-2 bg-transparent"
              >
                <FolderPlus className="h-5 w-5" />
                Save to Folder
              </Button>
            ) : tier === "free" ? (
              <Button
                variant="outline"
                onClick={onUpgrade}
                className="w-full min-h-[48px] border-[var(--border-medium)] hover:bg-[var(--surface)] rounded-lg gap-2 opacity-70 bg-transparent"
              >
                <Lock className="h-4 w-4" />
                Save to Folder
                <span className="ml-auto text-xs text-[var(--cta)]">Upgrade to Starter</span>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
